import { useEffect, useState } from 'react';
import { DISCIPLINE_LABEL } from '@flm9/i18n/labels';
import type { PlannedStageInfo } from '../lib/api';
import { errorText } from '../lib/errorText';

interface Props {
  stage: PlannedStageInfo['stage'];
  /** Available disciplines (pistol / pistol_carbine / rifle). */
  disciplines: Array<{ id: string; code: string }>;
  /** Load error of the discipline catalogue (App), shown with a retry. */
  disciplinesError?: string | null;
  onRetryDisciplines?: () => void;
  onConfirm: (disciplineId: string) => Promise<void> | void;
  onBack: () => void;
}

/**
 * Discipline confirm step shown when the referee taps "Starten" on a
 * planned stage. The main library flow picks discipline in the ParPicker;
 * planned stages skip that flow, so we always confirm the discipline here
 * before activating — guaranteeing every shot stage carries one (so it
 * shows up in statistics + the WaffG log). Pre-fills from the planned
 * stage's existing discipline (set during planning) else the first option.
 */
export function PlannedDisciplinePicker({
  stage,
  disciplines,
  disciplinesError = null,
  onRetryDisciplines,
  onConfirm,
  onBack,
}: Props) {
  const [picked, setPicked] = useState<string>(
    () => stage.disciplineId ?? disciplines[0]?.id ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same as ParPicker: App can't tell "still loading" from "loaded, but
  // empty" — after 8 s say so and offer a retry. `waitRound` restarts it.
  const [waitRound, setWaitRound] = useState(0);
  const [waitedLong, setWaitedLong] = useState(false);
  useEffect(() => {
    if (disciplines.length > 0) return;
    setWaitedLong(false);
    const t = window.setTimeout(() => setWaitedLong(true), 8_000);
    return () => window.clearTimeout(t);
  }, [disciplines.length, waitRound]);

  const distanceLabel = `${(stage.distanceCm / 100).toLocaleString('de-AT', {
    maximumFractionDigits: 2,
  })} m`;

  async function start() {
    if (picked === '') return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(picked);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onBack} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← zurück
        </button>
        <h2 className="text-lg font-bold">Disziplin wählen</h2>
      </header>

      <p className="text-sm text-zinc-400">
        <span className="font-medium text-zinc-200">
          Stage {stage.ord} · {stage.name}
        </span>{' '}
        · {stage.scoringMode === 'speed' ? 'Speed' : 'Präzision'} · {stage.shotCount} Schuss ·{' '}
        {distanceLabel}
      </p>

      {error && (
        <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Fehler: {error}</p>
          <button
            type="button"
            onClick={() => void start()}
            disabled={busy}
            className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40 disabled:opacity-50"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {disciplines.length === 0 && disciplinesError ? (
        <div role="alert" className="space-y-2 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Disziplinen konnten nicht geladen werden: {disciplinesError}</p>
          {onRetryDisciplines && (
            <button
              type="button"
              onClick={onRetryDisciplines}
              className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      ) : disciplines.length === 0 && waitedLong ? (
        <div role="alert" className="space-y-2 rounded bg-red-900/30 p-3 text-sm text-red-300">
          <p>Keine Disziplinen hinterlegt.</p>
          {onRetryDisciplines && (
            <button
              type="button"
              onClick={() => {
                onRetryDisciplines();
                setWaitRound((n) => n + 1);
              }}
              className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100"
            >
              Erneut versuchen
            </button>
          )}
        </div>
      ) : disciplines.length === 0 ? (
        <p className="text-sm text-amber-300">Disziplinen werden geladen…</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {disciplines.map((d) => {
            const active = picked === d.id;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => setPicked(d.id)}
                className={`rounded-lg border px-3 py-4 text-base font-semibold transition-colors ${
                  active
                    ? 'border-sky-500 bg-sky-700 text-white'
                    : 'border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500'
                }`}
              >
                {DISCIPLINE_LABEL[d.code] ?? d.code}
              </button>
            );
          })}
        </div>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={() => void start()}
          disabled={picked === '' || busy}
          className="w-full rounded-lg bg-emerald-700 px-4 py-4 text-lg font-bold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? '⏳ Starte…' : 'Starten'}
        </button>
      </div>
    </section>
  );
}
